import { useS3Image } from '../../hooks/useS3Image';
import { useUploadWithExisting } from '../../hooks/useUploadWithExisting';
import { cn } from '../../lib/utils';
import { getMediaType } from '../../utils/mediaUtils';
import * as Form from '../Form';
import { Skeleton } from '../portal';
import { S3Image } from '../S3Image';

export interface UploadWithExistingProps {
  mediaType: 'image' | 'media';
  name: string;
  label?: string;
  required?: boolean;
  description?: string;
  dimensions?: string;
  value: File | null;
  existingUrl: string | null;
  onChange: (file: File | null) => void;
  onRemoveExisting?: () => void;
  buttonType?: 'add' | 'upload';
  buttonClassName?: string;
  placeholderClassName?: string;
  className?: string;
  maxSize?: number;
}

const MEDIA_CLASS = cn(
  'block h-full w-full object-cover',
  Form.MEDIA_FADE_IN_CLASS,
);

/** Saved asset for the upload slot: presigned image, or video (skeleton while presigning). */
function ExistingMedia({
  mediaType,
  url,
  alt,
}: {
  mediaType: 'image' | 'media';
  url: string;
  alt: string;
}) {
  const resolvedMediaType = mediaType === 'image' ? 'image' : getMediaType(url);
  const { imageUrl: videoUrl, isLoading: isVideoLoading } = useS3Image(
    resolvedMediaType === 'video' ? url : null,
  );

  if (resolvedMediaType !== 'video') {
    return (
      <S3Image
        src={url}
        alt={alt}
        onLoad={Form.revealMedia}
        onError={Form.revealMedia}
        className={MEDIA_CLASS}
      />
    );
  }

  if (isVideoLoading) {
    return <Skeleton className="h-full w-full" rounded="lg" />;
  }

  return (
    <video
      controls
      autoPlay
      muted
      loop
      playsInline
      onLoadedData={Form.revealMedia}
      className={MEDIA_CLASS}
    >
      <source src={videoUrl || url} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  );
}

export function UploadWithExisting({
  mediaType,
  name,
  label,
  required,
  description,
  dimensions,
  value,
  existingUrl,
  onChange,
  onRemoveExisting,
  buttonType = 'add',
  buttonClassName,
  placeholderClassName,
  className,
  maxSize,
}: UploadWithExistingProps) {
  const { showExisting, handleRemoveExisting } = useUploadWithExisting({
    value,
    existingUrl,
    onRemoveExisting,
  });

  const shared = {
    name,
    label,
    required,
    description,
    dimensions,
    value,
    onChange,
    buttonType,
    buttonClassName,
    placeholderClassName,
    className,
    onRemoveExisting: handleRemoveExisting,
    existingPreview:
      showExisting && existingUrl ? (
        <ExistingMedia
          mediaType={mediaType}
          url={existingUrl}
          alt={`${label || name} preview`}
        />
      ) : undefined,
  };

  return mediaType === 'image' ? (
    <Form.ImageUploadInput {...shared} maxSize={maxSize} />
  ) : (
    <Form.MediaUploadInput {...shared} />
  );
}
